import { Tag, Typography } from "antd";
import { useMemo } from "react";
import {
  dateSorter,
  formatCareDateTime,
  formatRelationUserName,
  jsonPreview,
  coerceOpeningHoursRecord,
  coerceClosingDaysList,
  isOpeningHoursShape,
  numSorter,
  selectFilters,
  strSorter,
  summarizeOpeningHours,
  summarizeClosingDays,
  coerceStringArray,
  formatConcernLabel,
  formatProductLabel,
} from "./tableColumnUtils";
import FilterValueTag from "./FilterValueTag";

/** Pass to `useTableColumns(CARE_TABLE_KIND.doctors)` etc. */
export const CARE_TABLE_KIND = Object.freeze({
  doctors: "doctors",
  patients: "patients",
  pharmacy: "pharmacy",
  transactions: "transactions",
  staffAccess: "staffAccess",
  supportLogs: "supportLogs",
});

const STATUS_OPTIONS = ["pending", "approved", "rejected"];
const TIER_OPTIONS = ["rmp", "clinic", "professional", "specialist"];
const MARITAL_OPTIONS = ["single", "married", "divorced", "widowed", "prefer_not_to_say"];
const SMOKING_OPTIONS = ["never", "former", "occasionally", "current"];
const ALCOHOL_OPTIONS = ["never", "occasional", "regular"];

const BADGE_DOCTOR_STATUS = {
  pending: "gold",
  approved: "green",
  rejected: "red",
};

const BADGE_PRACTITIONER_TIER = {
  rmp: "default",
  clinic: "blue",
  professional: "geekblue",
  specialist: "purple",
};

const BADGE_PACKAGE_SETUP = { true: "green", false: "default" };

const BADGE_MARITAL = {
  single: "blue",
  married: "green",
  divorced: "orange",
  widowed: "purple",
  prefer_not_to_say: "default",
};

const BADGE_SMOKING = {
  never: "green",
  former: "cyan",
  occasionally: "gold",
  current: "red",
};

const BADGE_ALCOHOL = {
  never: "cyan",
  occasional: "gold",
  regular: "volcano",
};

const BADGE_USER_VERIFIED = {
  true: "green",
  false: "orange",
};

const BADGE_TX_STATUS = {
  success: "green",
  paid: "green",
  pending: "gold",
  failed: "red",
  unknown: "default",
};

const getDeepField = (data, field) => {
  const fields = field.split(".");
  let acc = data;
  for (const curr of fields) {
    if (acc == null || typeof acc !== "object") return undefined;
    acc = acc[curr];
  }
  return acc;
};

function formatPhoneDisplay(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    const s = String(raw).trim();
    return s === "" ? "" : s;
  }
  if (typeof raw === "object") {
    if (Array.isArray(raw)) {
      const parts = raw.map(formatPhoneDisplay).filter((x) => x !== "");
      return parts.length ? parts.join(", ") : "";
    }
    const pick = raw.phone ?? raw.number ?? raw.mobile ?? raw.tel ?? raw.national_number;
    if (pick != null && String(pick).trim() !== "") return String(pick).trim();
    return Object.keys(raw).length === 0 ? "" : jsonPreview(raw, 48);
  }
  return "";
}

function normalizePackageTemplate(tpl, index) {
  const slotNum = tpl?.slot != null && tpl.slot !== "" ? tpl.slot : index + 1;
  const amount = Number(tpl?.total_amount_inr);
  const amountLabel = Number.isFinite(amount)
    ? `INR ${amount.toLocaleString("en-IN")}`
    : String(tpl?.total_amount_inr ?? "");
  return {
    key: `${slotNum}-${amountLabel}`,
    chipLabel: `S${slotNum} · ${amountLabel}`,
    fullLabel: `Slot ${slotNum} - ${amountLabel}`,
  };
}

function renderPackageTemplates(value) {
  if (!Array.isArray(value) || value.length === 0) return "";
  const normalized = value.map(normalizePackageTemplate);

  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {normalized.map((tpl) => (
        <Tag key={tpl.key} color="cyan">
          {tpl.chipLabel}
        </Tag>
      ))}
    </div>
  );
}

function renderConcerns(value) {
  const list = coerceStringArray(value);
  if (list === null) return jsonPreview(value, 64);
  const labels = list.map((item) => formatConcernLabel(item)).filter(Boolean);
  if (labels.length === 0) return "";
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      {labels.map((label, i) => (
        <Tag key={`${i}-${label}`} color="geekblue">
          {label}
        </Tag>
      ))}
    </div>
  );
}

function renderPharmacyProducts(value) {
  const list = coerceStringArray(value);
  if (list === null) return jsonPreview(value, 72);
  const labels = list.map((item) => formatProductLabel(item)).filter(Boolean);
  if (labels.length === 0) return "";
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      {labels.map((label, i) => (
        <Tag key={`${i}-${label}`} color="cyan">
          {label}
        </Tag>
      ))}
    </div>
  );
}

function columnId(options = {}) {
  const { title = "ID", width: widthOpt, ...rest } = options;
  const hasWidth = Object.prototype.hasOwnProperty.call(options, "width");
  const width = hasWidth ? widthOpt : 120;
  return {
    title,
    dataIndex: "id",
    key: "id",
    ellipsis: true,
    ...(width != null ? { width } : {}),
    ...rest,
  };
}

function columnCreated() {
  return {
    title: "Created",
    dataIndex: "created",
    key: "created",
    width: 220,
    render: (v) => formatCareDateTime(v),
    sorter: dateSorter("created"),
  };
}

function columnUpdated() {
  return {
    title: "Updated",
    dataIndex: "updated",
    key: "updated",
    width: 220,
    render: (v) => formatCareDateTime(v),
    sorter: dateSorter("updated"),
  };
}

function columnRelationUserName(field, title, { sortable = false } = {}) {
  const col = {
    title,
    dataIndex: field,
    key: field,
    width: 200,
    ellipsis: true,
    render: (_, record) => formatRelationUserName(record[field]),
  };
  if (sortable) {
    col.sorter = (a, b) =>
      String(formatRelationUserName(a[field])).localeCompare(
        String(formatRelationUserName(b[field])),
      );
  }
  return col;
}

function columnDistrict() {
  return {
    title: "District",
    dataIndex: "district",
    key: "district",
    ellipsis: true,
    sorter: strSorter("district"),
  };
}

function columnState() {
  return {
    title: "State",
    dataIndex: "state",
    key: "state",
    ellipsis: true,
    sorter: strSorter("state"),
  };
}

function columnPhone(key = "user.phone", isExpand = true) {
  const dataIndex = isExpand ? `expand.${key}` : key;
  return {
    title: "Phone",
    dataIndex,
    key: dataIndex,
    width: 130,
    render: (_, record) => formatPhoneDisplay(getDeepField(record, dataIndex)),
  };
}

function columnLanguage({ ellipsis = true } = {}) {
  return {
    title: "Language",
    dataIndex: "language",
    key: "language",
    width: 100,
    ...(ellipsis ? { ellipsis: true } : {}),
    sorter: strSorter("language"),
  };
}

function buildDoctorColumns() {
  return [
    columnId({ title: "Profile ID", width: 160 }),
    {
      title: "Email",
      dataIndex: "user_email",
      key: "user_email",
      width: 220,
      ellipsis: true,
      sorter: strSorter("user_email"),
      render: (v) => (v ? String(v) : ""),
    },
    columnRelationUserName("user", "Name"),
    columnPhone(),
    {
      title: "Verified",
      dataIndex: "user_verified",
      key: "user_verified",
      width: 100,
      render: (v) => <FilterValueTag value={Boolean(v)} colorMap={BADGE_USER_VERIFIED} />,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => Boolean(record.user_verified) === value,
      sorter: (a, b) => Number(Boolean(a.user_verified)) - Number(Boolean(b.user_verified)),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (v) => <FilterValueTag value={v} colorMap={BADGE_DOCTOR_STATUS} />,
      filters: selectFilters(STATUS_OPTIONS),
      onFilter: (value, record) => record.status === value,
      sorter: strSorter("status"),
    },
    {
      title: "Specialty",
      dataIndex: "specialty",
      key: "specialty",
      ellipsis: true,
      sorter: strSorter("specialty"),
    },
    {
      title: "Clinic / hospital",
      dataIndex: "clinic_or_hospital",
      key: "clinic_or_hospital",
      ellipsis: true,
      sorter: strSorter("clinic_or_hospital"),
    },
    {
      title: "Concerns",
      dataIndex: "concerns",
      key: "concerns",
      width: 200,
      ellipsis: true,
      render: (v) => renderConcerns(v),
    },
    {
      title: "Package templates",
      dataIndex: "package_templates",
      key: "package_templates",
      width: 280,
      ellipsis: true,
      render: (v) => renderPackageTemplates(v),
    },
    {
      title: "Package setup",
      dataIndex: "package_setup",
      key: "package_setup",
      width: 120,
      render: (v) => <FilterValueTag value={Boolean(v)} colorMap={BADGE_PACKAGE_SETUP} />,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => Boolean(record.package_setup) === value,
    },
    {
      title: "Practitioner tier",
      dataIndex: "practitioner_tier",
      key: "practitioner_tier",
      width: 140,
      render: (v) => <FilterValueTag value={v} colorMap={BADGE_PRACTITIONER_TIER} />,
      filters: selectFilters(TIER_OPTIONS),
      onFilter: (value, record) => record.practitioner_tier === value,
      sorter: strSorter("practitioner_tier"),
    },
    {
      title: "Coin balance",
      dataIndex: "coin_balance",
      key: "coin_balance",
      width: 120,
      sorter: numSorter("coin_balance"),
    },
    columnLanguage(),
    columnCreated(),
    columnUpdated(),
  ];
}

function buildPatientColumns() {
  return [
    columnId({ title: "Profile ID", width: 160 }),
    {
      title: "Avatar",
      dataIndex: "avatar_url",
      key: "avatar_url",
      width: 88,
      render: (url, row) =>
        url ? (
          <img
            src={url}
            alt={row.user_name || "Patient avatar"}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              objectFit: "cover",
              border: "1px solid #e2e8f0",
            }}
          />
        ) : (
          ""
        ),
    },
    {
      title: "Email",
      dataIndex: "user_email",
      key: "user_email",
      width: 220,
      ellipsis: true,
      sorter: strSorter("user_email"),
      render: (v) => (v ? String(v) : ""),
    },
    columnRelationUserName("user", "Name"),
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      width: 100,
      sorter: strSorter("gender"),
    },
    {
      title: "Age",
      dataIndex: "age",
      key: "age",
      width: 80,
      sorter: numSorter("age"),
    },
    columnPhone(),
    {
      title: "Verified",
      dataIndex: "user_verified",
      key: "user_verified",
      width: 100,
      render: (v) => <FilterValueTag value={Boolean(v)} colorMap={BADGE_USER_VERIFIED} />,
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => Boolean(record.user_verified) === value,
      sorter: (a, b) => Number(Boolean(a.user_verified)) - Number(Boolean(b.user_verified)),
    },
    {
      title: "Primary condition",
      dataIndex: "primary_condition",
      key: "primary_condition",
      ellipsis: true,
      sorter: strSorter("primary_condition"),
    },
    {
      title: "Weight (kg)",
      dataIndex: "weight_kg",
      key: "weight_kg",
      width: 110,
      sorter: numSorter("weight_kg"),
    },
    {
      title: "Height (cm)",
      dataIndex: "height_cm",
      key: "height_cm",
      width: 110,
      sorter: numSorter("height_cm"),
    },
    {
      title: "Marital status",
      dataIndex: "marital_status",
      key: "marital_status",
      width: 160,
      render: (v) => <FilterValueTag value={v} colorMap={BADGE_MARITAL} />,
      filters: selectFilters(MARITAL_OPTIONS),
      onFilter: (value, record) => record.marital_status === value,
      sorter: strSorter("marital_status"),
    },
    columnDistrict(),
    columnState(),
    {
      title: "Smoking",
      dataIndex: "smoking",
      key: "smoking",
      width: 120,
      render: (v) => <FilterValueTag value={v} colorMap={BADGE_SMOKING} />,
      filters: selectFilters(SMOKING_OPTIONS),
      onFilter: (value, record) => record.smoking === value,
      sorter: strSorter("smoking"),
    },
    {
      title: "Alcohol",
      dataIndex: "alcohol",
      key: "alcohol",
      width: 120,
      render: (v) => <FilterValueTag value={v} colorMap={BADGE_ALCOHOL} />,
      filters: selectFilters(ALCOHOL_OPTIONS),
      onFilter: (value, record) => record.alcohol === value,
      sorter: strSorter("alcohol"),
    },
    {
      title: "Medical conditions",
      dataIndex: "medical_conditions",
      key: "medical_conditions",
      width: 180,
      ellipsis: true,
      render: (t) => jsonPreview(t, 80),
    },
    {
      title: "Allergies",
      dataIndex: "allergies",
      key: "allergies",
      width: 160,
      ellipsis: true,
      render: (t) => jsonPreview(t, 80),
    },
    {
      title: "Care mode",
      dataIndex: "care_mode",
      key: "care_mode",
      ellipsis: true,
      sorter: strSorter("care_mode"),
      render: (v) => <FilterValueTag value={v} />,
    },
    columnRelationUserName("preferred_quick_doctor", "Preferred quick doctor"),
    columnRelationUserName("preferred_quick_provider", "Preferred quick provider"),
    columnLanguage({ ellipsis: false }),
    columnCreated(),
    columnUpdated(),
  ];
}

function buildPharmacyColumns() {
  return [
    columnId({ title: "Profile ID", width: 160 }),
    {
      title: "Store name",
      dataIndex: "store_name",
      key: "store_name",
      ellipsis: true,
      sorter: strSorter("store_name"),
    },
    {
      title: "Tagline",
      dataIndex: "tagline",
      key: "tagline",
      ellipsis: true,
      sorter: strSorter("tagline"),
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 200,
      ellipsis: true,
      sorter: strSorter("address"),
    },
    columnDistrict(),
    columnState(),
    columnPhone("phone", false),
    {
      title: "Opening hours",
      dataIndex: "opening_hours",
      key: "opening_hours",
      width: 240,
      ellipsis: true,
      render: (v) => {
        const rec = coerceOpeningHoursRecord(v);
        if (!rec || !isOpeningHoursShape(rec)) return jsonPreview(v, 72);
        const out = summarizeOpeningHours(rec);
        return (
          <Typography.Text
            type="secondary"
            ellipsis={{ tooltip: <span style={{ whiteSpace: "pre-line" }}>{out.detail}</span> }}
            style={{ fontSize: 12, display: "block", maxWidth: "100%" }}
          >
            {out.summary}
          </Typography.Text>
        );
      },
    },
    {
      title: "Closing days",
      dataIndex: "closing_days",
      key: "closing_days",
      width: 140,
      ellipsis: true,
      render: (v) => {
        const list = coerceClosingDaysList(v);
        if (list === null) return jsonPreview(v, 72);
        const out = summarizeClosingDays(list);
        return (
          <Typography.Text
            type="secondary"
            ellipsis={
              out.tooltip
                ? { tooltip: <span style={{ whiteSpace: "pre-line" }}>{out.tooltip}</span> }
                : true
            }
            style={{ fontSize: 12, display: "block", maxWidth: "100%" }}
          >
            {out.summary}
          </Typography.Text>
        );
      },
    },
    {
      title: "Products",
      dataIndex: "products",
      key: "products",
      width: 220,
      ellipsis: true,
      render: (v) => renderPharmacyProducts(v),
    },
    columnCreated(),
    columnUpdated(),
  ];
}

function buildTransactionsColumns() {
  return [
    columnId({ title: "Transaction ID", width: undefined }),
    {
      title: "Patient",
      dataIndex: "patientName",
      key: "patientName",
      ellipsis: true,
      sorter: strSorter("patientName"),
    },
    {
      title: "Doctor",
      dataIndex: "doctorName",
      key: "doctorName",
      width: 160,
      ellipsis: true,
      sorter: strSorter("doctorName"),
    },
    {
      title: "Kind",
      dataIndex: "kind",
      key: "kind",
      width: 130,
      ellipsis: true,
      render: (v) => {
        const s = v != null && String(v).trim() !== "" ? String(v) : "";
        return s ? formatConcernLabel(s) : "—";
      },
      sorter: strSorter("kind"),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 130,
      render: (_, row) =>
        row.amountText != null
          ? row.amountText
          : `₹${Number(row.amount || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}`,
      sorter: (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <FilterValueTag
          value={status != null ? String(status).toLowerCase() : status}
          colorMap={BADGE_TX_STATUS}
        />
      ),
      filters: ["success", "paid", "pending", "failed", "unknown"].map((v) => ({
        text: v,
        value: v,
      })),
      onFilter: (value, record) => String(record.status || "").toLowerCase() === value,
      sorter: strSorter("status"),
    },
    {
      title: "Paid at",
      dataIndex: "transactionAt",
      key: "transactionAt",
      width: 200,
      render: (v, row) => row.transactionAtText || formatCareDateTime(v || row.created),
      sorter: dateSorter("transactionAt"),
    },
    columnCreated(),
    columnUpdated(),
  ];
}

function buildStaffAccessColumns() {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      sorter: strSorter("name"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      sorter: strSorter("email"),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 140,
      sorter: strSorter("role"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      sorter: strSorter("status"),
    },
    columnCreated(),
    columnUpdated(),
  ];
}

function buildSupportLogsColumns() {
  return [
    columnId({ title: "Log ID", width: undefined }),
    {
      title: "Staff",
      dataIndex: "staffEmail",
      key: "staffEmail",
      ellipsis: true,
      sorter: strSorter("staffEmail"),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      ellipsis: true,
      sorter: strSorter("action"),
    },
    {
      title: "Date & time",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (v, row) => row.createdAtText || formatCareDateTime(v || row.created),
      sorter: dateSorter("createdAt"),
    },
    columnCreated(),
    columnUpdated(),
  ];
}

const COLUMN_BUILDERS = {
  [CARE_TABLE_KIND.doctors]: buildDoctorColumns,
  [CARE_TABLE_KIND.patients]: buildPatientColumns,
  [CARE_TABLE_KIND.pharmacy]: buildPharmacyColumns,
  [CARE_TABLE_KIND.transactions]: buildTransactionsColumns,
  [CARE_TABLE_KIND.staffAccess]: buildStaffAccessColumns,
  [CARE_TABLE_KIND.supportLogs]: buildSupportLogsColumns,
};

/** @param {string} kind One of `CARE_TABLE_KIND` values */
export function useTableColumns(kind) {
  return useMemo(() => {
    const build = COLUMN_BUILDERS[kind];
    if (!build) {
      throw new Error(`useTableColumns: unknown kind "${kind}". Use CARE_TABLE_KIND.*`);
    }
    return build();
  }, [kind]);
}
